import { useCallback } from "react";

import { type InterviewTime } from "../components/InterviewTimeSelector";

interface StoredRecruiterCallTime {
  start: string; // ISO string
  end: string; // ISO string
}

function getStorageKey(placementId: string): string {
  return `recruiter-call-time-${placementId}`;
}

/**
 * Hook to manage persisted recruiter call time selection for screen_last cohort users.
 * Stores recruiter call time in localStorage so it can be restored after screening is completed.
 *
 * Silently fails if localStorage is not available.
 */
export function useRecruiterCallTimeStorage() {
  const saveRecruiterCallTime = useCallback((placementId: string, time: InterviewTime): void => {
    try {
      const storageKey = getStorageKey(placementId);
      const storedValue: StoredRecruiterCallTime = {
        start: time.start.toISOString(),
        end: time.end.toISOString(),
      };
      localStorage.setItem(storageKey, JSON.stringify(storedValue));
    } catch {
      // Ignore storage errors
    }
  }, []);

  const loadRecruiterCallTime = useCallback((placementId: string): InterviewTime | undefined => {
    try {
      const storageKey = getStorageKey(placementId);
      const raw = localStorage.getItem(storageKey);
      if (!raw) {
        return undefined;
      }

      const storedValue = JSON.parse(raw) as StoredRecruiterCallTime;
      return {
        start: new Date(storedValue.start),
        end: new Date(storedValue.end),
      };
    } catch {
      return undefined;
    }
  }, []);

  const clearRecruiterCallTime = useCallback((placementId: string): void => {
    try {
      const storageKey = getStorageKey(placementId);
      localStorage.removeItem(storageKey);
    } catch {
      // Ignore storage errors
    }
  }, []);

  return {
    saveRecruiterCallTime,
    loadRecruiterCallTime,
    clearRecruiterCallTime,
  };
}
